import React, { useState } from 'react';

import classNames from 'classnames';
import { Prompt } from 'react-router-dom';
import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';
import { faExclamationCircle, faPlus } from '@fortawesome/pro-solid-svg-icons';

import Button from 'shared/button';
import EmptyState from 'shared/empty_state';
import Filters from './filters';
import LoadingSpinner from 'components/loading_spinner';
import Task from './task';
import TaskSidebarModal from 'bundles/apex/tasks/task_modal/sidebar';
import { isFilterEnabled } from './filters/helpers';
import { useTasksState } from './helpers';

import type { ForUser, Task as TaskType, TaskAttributes } from 'bundles/apex/tasks/types';
import type { Parent } from 'bundles/crm/types';
import type { Props as FiltersProps } from './filters';
import type { FilterProps } from './filters/types';
import type { Props as TaskProps } from './task';

import './styles.scss';

// TODO: Consistent props (hide vs show) would be nice...
export interface Props {
  currentParent?: Parent,
  inline?: boolean;
  features?: TaskProps['features'];
  filters: Omit<FiltersProps, 'children' | 'oneLine' | 'onChange' | 'onChangePending'>;
  forUser: ForUser;
  hideAssignees?: boolean;
  options?: {
    assignees?: [];
    parents?: [];
  };
  paginate?: boolean;
  paths: {
    attachments: string;
    checkForImmediateReminders: string;
    create?: string;
    formDependenciesPath: string;
  };
  showParents?: TaskProps['showParents'];
  showRouterPrompt?: boolean;
  tasks?: TaskType[];
}

export default function Tasks({
  currentParent,
  features,
  filters: filtersProps,
  forUser,
  hideAssignees = false,
  inline,
  options = {},
  paginate,
  paths,
  showParents,
  showRouterPrompt,
  tasks: tasksProp,
}: Props) {
  const [showModal, setShowModal] = useState(false);

  const {
    selectedTask,
    setSelectedTask,
    tasks,

    setFilters,

    loading,
    setLoading,

    pagination,
    setPage,

    addTaskAttachment,
    createTask,
    deleteTask,
    reloadTasks,
    removeTaskAttachment,
    updateTask,
  } = useTasksState({
    dateRangeEnabled: isFilterEnabled(filtersProps as Record<string, FilterProps>, 'dateRange'),
    paginate,
    paths,
    tasks: tasksProp,
  });

  function showTaskModal(task?: TaskType) {
    setSelectedTask(task || null);
    setShowModal(true);
  }

  let body, emptyState, pageControls;
  if (loading) {
    body = (
      <tr>
        <td colSpan={99}>
          <div className="mb-4">
            <LoadingSpinner/>
          </div>
        </td>
      </tr>
    );
  } else {
    body = <>
      {tasks.map(task => (
        <Task
          active={task.id === selectedTask?.id && showModal}
          addTaskAttachment={addTaskAttachment}
          attachmentsPath={paths.attachments}
          currentParent={currentParent}
          deleteTaskAttachment={removeTaskAttachment}
          features={features}
          forUser={forUser}
          hideAssignee={hideAssignees}
          key={task.id}
          modalOpen={showModal}
          options={options}
          showParents={showParents}
          task={task}
          onClick={showTaskModal}
          onDelete={deleteTask}
          onUpdate={updateTask}
        />
      ))}
    </>;

    if (pagination && pagination.totalPages > 1) {
      pageControls = (
        <div className="text-center">
          <UltimatePagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onChange={setPage}
          />
        </div>
      );
    }

    if (tasks.length === 0) {
      emptyState = (
        <EmptyState
          bodyText={
            paths?.create
              ? 'Create your first task to get started'
              : 'Tasks created via Contacts, Deals or Listings will populate here'
          }
          buttonIcon={faPlus}
          buttonText="New Task"
          header="No tasks to display"
          icon={faExclamationCircle}
          plainButton={false}
          onClick={paths?.create ? () => showTaskModal() : undefined}
        />
      );

      if (inline) {
        emptyState = <div className="mx-4">{emptyState}</div>;
      }
    }
  }

  // The modal uses the current task state for some things (e.g. attachments), so storing the task
  // in state is no good - always pass the current value in. However, doing only that is problematic
  // during pagination when the task lookup is temporarily null, so store and use both.
  // TODO: Obviously, this is very messy - clean up how the modal works
  const currentModalTask = tasks.find(task => task.id === selectedTask?.id);
  const modalTaskIndex = currentModalTask ? tasks.indexOf(currentModalTask) : -1;
  let hasPrevious = false, hasNext = false;
  if (currentModalTask) {
    hasPrevious = modalTaskIndex > 0 || (!!pagination && pagination.currentPage > 1);
    hasNext = modalTaskIndex < tasks.length - 1 || (!!pagination && pagination.currentPage < pagination.totalPages);
  }

  async function selectPreviousTask() {
    if (modalTaskIndex > 0) {
      showTaskModal(tasks[modalTaskIndex - 1]);
    } else if (pagination) {
      const newTasks = await setPage(pagination.currentPage - 1);
      showTaskModal(newTasks[newTasks.length - 1]);
    }
  }

  async function selectNextTask() {
    if (modalTaskIndex < tasks.length - 1) {
      showTaskModal(tasks[modalTaskIndex + 1]);
    } else if (pagination) {
      const newTasks = await setPage(pagination.currentPage + 1);
      showTaskModal(newTasks[0]);
    }
  }

  return (
    <div className={classNames('apex-tasks-list mb-5', { 'apex-tasks-list--inline': inline })}>
      <div className={classNames('mb-1', { 'mt-4 mx-4': inline })}>
        <Filters
          {...filtersProps}
          onChange={setFilters}
          onChangePending={() => setLoading(true)}
        >
          {paths?.create &&
            <div>
              <Button.Secondary
                icon={faPlus}
                label="New Task"
                onClick={() => showTaskModal()}
              />
            </div>
          }
        </Filters>
      </div>

      <table className="ds-table ds-table--light ds-table--bordered-rows table-hover">
        <thead>
          <th colSpan={2}><div className="pl-3">Task</div></th>
          {showParents && <th>Source</th>}
          <th>Due Date</th>
          {!hideAssignees && <th>Assignee</th>}
          <th>Attachments</th>
        </thead>
        <tbody>
          {body}
        </tbody>
      </table>

      {emptyState}
      {pageControls}

      <TaskSidebarModal
        attachmentsPath={selectedTask ? paths.attachments : undefined}
        canDeleteAttachments
        checkImmediateRemindersPath={paths.checkForImmediateReminders}
        createTask={task => createTask({ assigneeId: forUser.id, ...task } as TaskAttributes)}
        currentTask={currentModalTask}
        disableDelete={!selectedTask}
        formDependenciesPath={(selectedTask || paths).formDependenciesPath}
        forUser={forUser}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        hideAssignee={selectedTask ? selectedTask.parent?.private : hideAssignees}
        options={options}
        pageLoading={loading}
        reminders={selectedTask?.reminders?.filter(reminder => reminder.userId === forUser.id)}
        show={showModal}
        showType
        task={selectedTask}
        updateTask={updateTask}
        onAddAttachment={addTaskAttachment}
        onDeleteAttachment={removeTaskAttachment}
        onDelete={deleteTask}
        onHide={() => setShowModal(false)}
        onNext={selectNextTask}
        onPrevious={selectPreviousTask}
        reloadTasks={reloadTasks}
      />
      {showRouterPrompt && (
        <Prompt
          message="You have unsaved changes. Are you sure you want to leave this page?"
          when={showModal || false}
        />
      )}
    </div>
  );
}
